/**
 * Preferences provider.
 *
 * Seeds the preferences context from the session bootstrap payload
 * (so the first render has the user's saved working state without
 * an extra round-trip per REQ-AUTH-057) and supplies the
 * working-state mutators (:func:`setWorkingState`,
 * :func:`applyWorkingState`) plus the lower-level
 * :func:`setPreferences` / :func:`resetPreferences` the gear-menu
 * calls.
 *
 * **Preferences schema v3 (ADR-033 §3.2).** The live UI
 * configuration is the ``working_state`` — a full Profile document
 * (:type:`ProfileDocument`). ``active_profile`` points at the
 * Profile it was last applied from (``null`` = unnamed / custom).
 * The provider exposes both as first-class context fields; the rest
 * of the app reads slices of ``working_state`` (theme, layout,
 * entity-detail defaults, the dynamic-track map setting).
 *
 * The mutator semantics are **optimistic**:
 *
 * 1. Snapshot the current preferences.
 * 2. Update the in-memory state immediately (so the live working
 *    state stays current across re-renders).
 * 3. Apply the resolved theme to ``<html data-theme>`` via the
 *    :func:`useApplyTheme` hook (consumer-side; the provider
 *    itself only owns the value).
 * 4. Issue ``PUT /api/me/preferences`` with the v3 body.
 * 5. On success, write the response back to the localStorage
 *    cache so the next cold load avoids the flash-of-default-theme.
 * 6. On failure, roll back to the snapshot and surface the error
 *    to the caller for inline display.
 *
 * **Login default-profile resolution (ADR-033 §3.2 amended 2026-06-08;
 * REQ-UI-093).** On a **fresh sign-in only** — not a reload within the same
 * session — the provider resolves ``preferences.default_profile`` (the user's
 * chosen login-default) into the working state:
 *
 *   - **set + the Profile still exists** (found in the visible Shared + User
 *     list) → fetch its document and apply it (``applyWorkingState``).
 *   - **set + the Profile is gone** (a Shared Profile an admin deleted, or a
 *     User Profile the user removed) → apply the **built-in Shared default**
 *     and raise a **visible + announced warning** naming the missing Profile
 *     (operator-visibility, CLAUDE.md §8.1).
 *   - **unset (null)** → apply the built-in Shared default (no warning — the
 *     normal "no default chosen" case; subsumes the prior D9 first-run
 *     auto-apply, since a brand-new user's ``default_profile`` is null).
 *
 * A **reload / subsequent load within the same sign-in** does **not** re-apply:
 * the persisted working state (with the user's unsaved overrides) loads as-is
 * and survives the reload. Fresh-login vs. reload is told apart by a **per-user
 * marker in localStorage** (:mod:`preferences/default-applied-marker`) —
 * absent ⇒ fresh login (resolve + set it); present ⇒ same session (skip). The
 * marker is cleared on logout (in the api-client ``logout()`` path) so the next
 * sign-in re-resolves; ``localStorage`` (not ``sessionStorage``) means a second
 * tab in the same session does NOT re-apply over the working state. When no
 * built-in is available (a bootstrap that predates the ``profiles`` field), the
 * provider falls back to the local default working state without a PUT.
 *
 * Cross-references:
 *   - REQ-AUTH-054 (PUT endpoint contract).
 *   - REQ-AUTH-055 / 070 (schema versioning — we always PUT v3).
 *   - REQ-UI-042 / 043 (preferences menu).
 *   - REQ-UI-044 (cache write-back for pre-bootstrap script).
 *   - REQ-UI-073 / 075 (working-state model; Apply copies by value).
 *   - REQ-UI-074 (active-Profile pointer + "(modified)" marker).
 *   - REQ-UI-093 (default applied at login) / REQ-UI-094 (announced warning).
 *   - ADR-022 §Feature 3, ADR-033 §3.2 / §4.2 / §12.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@/auth/session-context";
import type {
  ActiveProfileRef,
  DefaultProfileRef,
  ProfileDocument,
  ProfileSummary,
  UserPreferences,
} from "@/auth/session-types";
import { ApiError, apiFetch } from "@/lib/api-client";
import {
  PreferencesContext,
  type PreferencesContextValue,
  type SetPreferencesResult,
} from "@/preferences/preferences-context";
import { buildDefaultWorkingState } from "@/preferences/default-working-state";
import { clearPreferencesCache, writePreferencesCache } from "@/preferences/cache";
import { hasDefaultApplied, markDefaultApplied } from "@/preferences/default-applied-marker";
import { defaultProfileMissingMessage } from "@/preferences/default-profile-warning";
import { useNotify } from "@/notifications/use-notify";
import { getProfile } from "@/profiles/profiles-client";
import { sha256CanonicalJson } from "@/profiles/profile-hash";
import { useApplyTheme } from "@/theme/apply-theme";

/**
 * Resolve the live working state from a (possibly empty or
 * partial) preferences document. The BFF forward-migrates v1/v2
 * to v3 on read, so in practice ``preferences.working_state`` is
 * present whenever a row exists; the default fallback covers the
 * empty-document (first-run / pre-auto-apply) case.
 */
function resolveWorkingState(prefs: UserPreferences): ProfileDocument {
  const ws = prefs.working_state;
  if (ws !== undefined && ws !== null && typeof ws === "object") {
    return ws;
  }
  return buildDefaultWorkingState();
}

/**
 * Build the v3 ``PUT /api/me/preferences`` body. Round-trips the
 * **full** working state (theme / units / clocks / map /
 * entity_details / layout) so a single-slice edit never drops
 * another increment's data (REQ-AUTH-060 round-trip discipline),
 * plus the ``active_profile`` pointer (``null`` when the working
 * state is unnamed / custom) and the ``default_profile`` login-default
 * pointer (round-tripped on every PUT so a working-state edit never
 * wipes the user's chosen login default — ADR-033 §3.2).
 *
 * The BFF validates ``working_state`` against ``ProfileDocumentV1``
 * (strict; ``dockview_state`` opaque), ``active_profile`` against
 * ``ActiveProfileRef``, and ``default_profile`` against
 * ``DefaultProfileRef`` — unknown top-level fields are rejected, so
 * we emit exactly the four v3 keys.
 */
function buildPutBody(
  workingState: ProfileDocument,
  activeProfile: ActiveProfileRef | null,
  defaultProfile: DefaultProfileRef | null,
): Record<string, unknown> {
  return {
    schema_version: 3,
    working_state: workingState,
    active_profile: activeProfile,
    default_profile: defaultProfile,
  };
}

/**
 * Find the built-in Shared (Global) default Profile in the bootstrap
 * ``profiles`` list — the ``scope === "global" && is_builtin``
 * entry the BFF seeds (ADR-033 §12). Returns ``null`` when the
 * list is absent (a bootstrap predating the field) or carries no
 * built-in; the caller then falls back to the local default
 * working state. This is the target for the unset-default and
 * missing-default login-resolution branches.
 */
function findBuiltinDefault(
  profiles: readonly ProfileSummary[] | undefined,
): ProfileSummary | null {
  if (profiles === undefined) {
    return null;
  }
  return profiles.find((p) => p.scope === "global" && p.is_builtin) ?? null;
}

/**
 * Does the user's chosen login-default Profile still exist in the visible
 * list (every Shared Profile + the caller's own User Profiles — REQ-AUTH-065)?
 * Matched by ``profile_id`` (the stable key; the stored ``name`` may have been
 * renamed since the pointer was set). Returns ``false`` when the list is
 * absent, so a bootstrap predating the ``profiles`` field treats the default as
 * "gone" and falls back to the built-in — the conservative direction.
 */
function defaultProfileExists(
  profiles: readonly ProfileSummary[] | undefined,
  ref: DefaultProfileRef,
): boolean {
  if (profiles === undefined) {
    return false;
  }
  return profiles.some((p) => p.profile_id === ref.profile_id);
}

interface PreferencesProviderProps {
  /** Bootstrap-payload preferences — seeds the initial state, including the
   *  user's chosen ``default_profile`` pointer that login resolution reads.
   *  ``{}`` (no fields) when the user has no preferences row — a brand-new
   *  user, whose ``default_profile`` is then null ⇒ the built-in default is
   *  applied on fresh login (ADR-033 §3.2). */
  initial: UserPreferences;
  /** Bootstrap-payload Profiles list — the visible Shared + own User Profiles
   *  (REQ-AUTH-065). Login resolution uses it both to **locate the built-in
   *  default** and to answer **"does the chosen default still exist?"**.
   *  Optional so a bootstrap predating the field degrades to the local
   *  default. */
  profiles?: readonly ProfileSummary[];
  children: ReactNode;
}

/**
 * The provider. Mounted by :class:`SessionProvider` once the
 * bootstrap response is available, so the initial preferences are
 * already in hand and the mount is synchronous.
 */
export function PreferencesProvider({
  initial,
  profiles,
  children,
}: PreferencesProviderProps): React.ReactElement {
  const session = useSession();
  const [preferences, setLocalPreferences] = useState<UserPreferences>(() =>
    normaliseInitial(initial),
  );

  // Settings are always live (architect 2026-06-08, part 1): ``workingState``
  // is the persisted working state directly — there is no preview overlay.
  // The Settings panel edits it through :func:`setWorkingState` (optimistic
  // persist), so every consumer (theme / units / clocks / map / entity-detail
  // / layout) already tracks the live edits with zero changes.
  const workingState = useMemo(() => resolveWorkingState(preferences), [preferences]);
  const activeProfile = preferences.active_profile ?? null;
  // The login-default Profile pointer (ADR-033 §3.2). Round-tripped on every
  // PUT; the gear menu's "Set as default" affordance writes it.
  const defaultProfile = preferences.default_profile ?? null;

  // Login-resolution warning (ADR-033 §3.2): raised when a fresh sign-in found
  // the user's chosen default Profile missing and fell back to the built-in
  // Shared default.
  //
  // Migrated 2026-07-26 (#726) from the bespoke ``<DefaultProfileWarning>``
  // banner this provider used to render to the shared Flow-F notification host,
  // so the app has ONE transient-feedback surface rather than two (CLAUDE.md
  // §4.4). That component existed only because no host did — its own docstring
  // said so. Two deliberate behaviour notes:
  //
  //   - It is raised ``sticky``, preserving the bespoke banner's persist-until-
  //     dismissed behaviour: the copy tells the operator to pick a new default
  //     in Settings, and an 8 s warning timer would lose that instruction.
  //   - Announcement politeness changes from ``role="alert"`` (assertive) to
  //     ``role="status"`` (polite), because the host derives the role from
  //     severity and this is a warning, not an error. REQ-UI-094 requires the
  //     affordance be *announced*, which both satisfy; the bespoke banner chose
  //     assertive because it was the only surface available, not because a
  //     missing default Profile warrants interrupting the operator.
  const notify = useNotify();

  // Drive ``<html data-theme>`` from the working state's theme.
  // Re-runs on every preferences update; also subscribes to the
  // ``prefers-color-scheme`` media query when the user picked
  // ``"system"`` per REQ-UI-041.
  useApplyTheme(workingState.theme ?? "system");

  // First-mount reconciliation: mirror the working-state theme +
  // layout to the localStorage cache so the inline pre-paint script
  // reads a truthful value next cold load. The inline script in
  // index.html already applied the cached value before paint; this
  // write makes the cache truthful again. Idempotent on agreement.
  //
  // ``hasBootstrapWorkingState`` gates this: when the bootstrap was
  // empty (first-run), we do NOT write a default-theme cache entry
  // here — the auto-apply effect below owns the first write once the
  // built-in default lands, so the cache reflects the applied
  // profile rather than the bare local default.
  const initialHadWorkingState =
    initial.working_state !== undefined && initial.working_state !== null;
  useEffect(() => {
    if (!initialHadWorkingState) {
      return;
    }
    writePreferencesCache(session.network, session.user.subject_id, {
      schema_version: 3,
      working_state: {
        theme: workingState.theme ?? "system",
        layout: workingState.layout,
      },
    });
    // Subject_id and network are stable for the life of a session;
    // we only reconcile on initial preferences value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Persist a (workingState, activeProfile, defaultProfile) triple
   * optimistically. Shared by every mutator: snapshot → set in-memory →
   * PUT → cache on success / roll back on failure. ``onSuccess`` (when
   * supplied) replaces the default localStorage-cache write (the reset path
   * passes a dedicated clear).
   *
   * ``nextDefaultProfile`` defaults to the current preferences'
   * ``default_profile`` so working-state edits / Apply / reset round-trip the
   * user's login-default pointer untouched; only :func:`setDefaultProfile`
   * passes a new value (ADR-033 §3.2).
   */
  const persist = useCallback(
    async (
      nextWorkingState: ProfileDocument,
      nextActiveProfile: ActiveProfileRef | null,
      nextDefaultProfile: DefaultProfileRef | null = preferences.default_profile ?? null,
      onSuccess?: () => void,
    ): Promise<SetPreferencesResult> => {
      const snapshot = preferences;
      const next: UserPreferences = {
        schema_version: 3,
        working_state: nextWorkingState,
        active_profile: nextActiveProfile,
        default_profile: nextDefaultProfile,
      };
      setLocalPreferences(next);
      try {
        await apiFetch("/api/me/preferences", {
          method: "PUT",
          body: JSON.stringify(
            buildPutBody(nextWorkingState, nextActiveProfile, nextDefaultProfile),
          ),
        });
        if (onSuccess !== undefined) {
          onSuccess();
        } else {
          writePreferencesCache(session.network, session.user.subject_id, {
            schema_version: 3,
            working_state: {
              theme: nextWorkingState.theme ?? "system",
              layout: nextWorkingState.layout,
            },
          });
        }
        return { status: "ok" };
      } catch (error) {
        // Roll back the optimistic state so the caller's menu
        // re-renders the previous selection alongside the error.
        setLocalPreferences(snapshot);
        return { status: "error", message: describeError(error) };
      }
    },
    [preferences, session.network, session.user.subject_id],
  );

  const setWorkingState = useCallback(
    (patch: Partial<ProfileDocument>): Promise<SetPreferencesResult> => {
      const merged: ProfileDocument = { ...workingState, ...patch };
      // Editing the working state away from the applied Profile keeps
      // the ``active_profile`` pointer; the "(modified)" marker is
      // derived (hash mismatch), so we do not clear the pointer here.
      //
      // NOTE (part-1 follow-up): now that the Settings panel edits live, a
      // rapid control (e.g. a colour slider dragged continuously) persists
      // per change — one PUT per intermediate value. A debounce on this
      // optimistic persist is a reasonable follow-up; it is deliberately not
      // built here to keep the in-memory state and the cache truthful on
      // every render. The optimistic in-memory update is synchronous either
      // way, so the canvas tracks the edit live regardless.
      return persist(merged, activeProfile);
    },
    [workingState, activeProfile, persist],
  );

  const applyWorkingState = useCallback(
    (document: ProfileDocument, ref: ActiveProfileRef): Promise<SetPreferencesResult> =>
      persist(document, ref),
    [persist],
  );

  const setPreferences = useCallback(
    (next: UserPreferences): Promise<SetPreferencesResult> => {
      const normalised = normaliseInitial(next);
      // Honour an explicit ``default_profile`` on the input; otherwise PRESERVE
      // the current login-default (``undefined`` ⇒ persist keeps it). The
      // import flow, e.g., replaces the whole document but must not wipe the
      // user's login default (ADR-033 §3.2).
      const nextDefault = "default_profile" in next ? (next.default_profile ?? null) : undefined;
      return persist(
        resolveWorkingState(normalised),
        normalised.active_profile ?? null,
        nextDefault,
      );
    },
    [persist],
  );

  const resetPreferences = useCallback((): Promise<SetPreferencesResult> => {
    // Reset = default working state, no active Profile, and clear
    // the localStorage cache (so a cold load applies the inline
    // script's fallback). Implemented as a PUT with defaults rather
    // than DELETE because the BFF surface only exposes GET/PUT today.
    // The login-default pointer (``default_profile``) is deliberately
    // PRESERVED — reset wipes the live config, not the user's chosen
    // login default (``undefined`` ⇒ persist keeps the current value).
    return persist(buildDefaultWorkingState(), null, undefined, () => {
      clearPreferencesCache(session.network, session.user.subject_id);
    });
  }, [persist, session.network, session.user.subject_id]);

  const setDefaultProfile = useCallback(
    (ref: DefaultProfileRef | null): Promise<SetPreferencesResult> =>
      // Working state + active-Profile pointer are unchanged; only the
      // login-default pointer moves (ADR-033 §3.2). Pass the current working
      // state + active profile so the optimistic in-memory state stays whole.
      persist(workingState, activeProfile, ref),
    [persist, workingState, activeProfile],
  );

  // Login default-profile resolution (ADR-033 §3.2 amended 2026-06-08;
  // REQ-UI-093). Runs ONCE per fresh sign-in — the per-user localStorage marker
  // tells a fresh login apart from a reload within the same session:
  //   - marker PRESENT ⇒ reload ⇒ skip (the persisted working state, with the
  //     user's unsaved overrides, loads as-is and survives the reload);
  //   - marker ABSENT ⇒ fresh login ⇒ resolve ``default_profile`` + set marker.
  // Resolution branches:
  //   - default SET + still exists ⇒ apply that Profile's document;
  //   - default SET + gone (admin-deleted Shared / user-removed User) ⇒ apply
  //     the built-in Shared default + raise a visible, announced warning;
  //   - default UNSET (null) ⇒ apply the built-in Shared default, no warning
  //     (subsumes the prior D9 first-run auto-apply — a new user's default is
  //     null). When no built-in is available (bootstrap predates ``profiles``),
  //     fall back to the local default working state without a PUT.
  // The marker is set synchronously at the start so a failed apply-PUT doesn't
  // cause the next reload to retry; the user keeps their working state and can
  // re-apply manually (best-effort, fail-safe per CLAUDE.md §8.1).
  const resolvedRef = useRef(false);
  useEffect(() => {
    if (resolvedRef.current) {
      return;
    }
    resolvedRef.current = true;
    const userId = session.user.subject_id;
    // Reload within the same sign-in → do not re-apply.
    if (hasDefaultApplied(userId)) {
      return;
    }
    // From here on this IS a fresh login; record it so subsequent loads skip.
    markDefaultApplied(userId);

    // Decide the target Profile to apply (synchronously), and whether the
    // chosen default was missing (⇒ warn).
    let targetId: string;
    if (defaultProfile !== null && defaultProfileExists(profiles, defaultProfile)) {
      // The chosen default still exists — apply it.
      targetId = defaultProfile.profile_id;
    } else {
      // Unset, or set-but-gone → the built-in Shared default. Warn only when a
      // default WAS chosen but has since vanished (naming it so the operator
      // recognises which Profile to replace).
      if (defaultProfile !== null) {
        notify({
          severity: "warning",
          title: defaultProfileMissingMessage(defaultProfile.name),
          source: "system",
          // Persist until dismissed — the copy is an instruction to act on.
          sticky: true,
          // One warning per resolution, even if the effect re-runs.
          dedupeKey: "default-profile-missing",
        });
      }
      const builtin = findBuiltinDefault(profiles);
      if (builtin === null) {
        // No built-in available — keep the local default working state; no PUT
        // (we have no Profile to point at). Degrade-safe.
        return;
      }
      targetId = builtin.profile_id;
    }

    let cancelled = false;
    void (async () => {
      try {
        const detail = await getProfile(targetId);
        if (cancelled) {
          return;
        }
        const doc = detail.document;
        const ref: ActiveProfileRef = {
          scope: detail.scope,
          profile_id: detail.profile_id,
          name: detail.name,
          applied_at: new Date().toISOString(),
          applied_document_sha256: await sha256CanonicalJson(doc),
        };
        if (cancelled) {
          return;
        }
        await applyWorkingState(doc, ref);
      } catch {
        // Resolution is best-effort: on a fetch/PUT failure the user keeps
        // their loaded working state (the marker is already set, so reload
        // won't retry). The next explicit Apply recovers. Non-fatal — the app
        // still renders the (default or persisted) working state.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run-once per mount; the resolvedRef + marker guard re-entry. The
    // bootstrap inputs (preferences, profiles, session) are stable for the
    // provider's life, so an empty dep array is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<PreferencesContextValue>(
    () => ({
      preferences,
      workingState,
      activeProfile,
      defaultProfile,
      setWorkingState,
      applyWorkingState,
      setPreferences,
      resetPreferences,
      setDefaultProfile,
    }),
    [
      preferences,
      workingState,
      activeProfile,
      defaultProfile,
      setWorkingState,
      applyWorkingState,
      setPreferences,
      resetPreferences,
      setDefaultProfile,
    ],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
      {/* Login-resolution warning (ADR-033 §3.2) — renders nothing unless a
       * fresh sign-in fell back to the built-in default because the chosen
       * default Profile was gone. role="alert" ⇒ visible AND announced
       * (REQ-UI-094); mounted here so it sits above the canvas and survives
       * route changes within the authenticated shell. */}
    </PreferencesContext.Provider>
  );
}

/**
 * Coerce a permissive preferences document into the v3 shape — used
 * both for the initial bootstrap value (where the BFF may return
 * ``{}``) and for ``setPreferences`` inputs (where a test may pass a
 * sparse document). Normalises ``schema_version`` to 3, resolves the
 * working state (defaulting theme to ``"dark"`` per the 2026-05-26
 * amendment when absent), and preserves ``active_profile``.
 */
function normaliseInitial(value: UserPreferences): UserPreferences {
  const ws = resolveWorkingState(value);
  const theme =
    ws.theme === "light" || ws.theme === "dark" || ws.theme === "system" ? ws.theme : "dark";
  return {
    schema_version: 3,
    working_state: { ...ws, theme },
    active_profile: value.active_profile ?? null,
    // Preserve the login-default pointer through normalisation so a bootstrap
    // that carries ``default_profile`` survives the initial reconcile + the
    // ``setPreferences`` escape hatch round-trips it (ADR-033 §3.2).
    default_profile: value.default_profile ?? null,
  };
}

/**
 * Coerce a thrown value into a user-facing message per REQ-UI-037.
 * The :class:`ApiError` carries a ``status`` and an opaque ``body``;
 * the menu renders a generic copy line so an operator never sees the
 * BFF's internal error text.
 */
function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 422) {
      return "We couldn't save these preferences — please try again.";
    }
    if (error.status >= 500) {
      return "Preferences not saved — server error. Try again or refresh the page.";
    }
    return "Preferences not saved — please try again.";
  }
  return "Preferences not saved — please try again.";
}
